import {
  KeyIcon,
  PlusIcon,
  ShieldCheckIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { type FormEvent, useCallback, useEffect, useState } from "react";

import { ApiError, apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { TextField } from "../components/Field";
import { LoadingState } from "../components/LoadingState";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import type { AdminUser } from "../types";

export function TeamPage() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    apiRequest<AdminUser[]>("/api/v1/admin/users")
      .then(setUsers)
      .catch(() => setError("Не удалось загрузить команду"));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="page">
      <PageHeader
        actions={
          <Button onClick={() => setShowCreate((value) => !value)}>
            <PlusIcon size={17} /> Добавить участника
          </Button>
        }
        description="Все участники имеют единый административный уровень без RBAC."
        eyebrow="Доступ"
        title="Команда"
      />
      {showCreate && (
        <CreateUser onCancel={() => setShowCreate(false)} onCreated={load} />
      )}
      {selected && (
        <EditUser
          onCancel={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            load();
          }}
          user={selected}
        />
      )}
      {error && <div className="notice notice--error">{error}</div>}
      {!users ? (
        <LoadingState />
      ) : (
        <section className="team-list">
          {users.map((user) => (
            <div className="team-row" key={user.id}>
              <span className="account-avatar account-avatar--large">
                {user.display_name.slice(0, 2).toUpperCase()}
              </span>
              <span className="team-row__identity">
                <strong>{user.display_name}</strong>
                <small>@{user.username}</small>
              </span>
              <span className="team-row__role">
                <ShieldCheckIcon size={17} /> Администратор
              </span>
              <StatusBadge status={user.is_active ? "active" : "inactive"}>
                {user.is_active ? "Активен" : "Отключён"}
              </StatusBadge>
              <span>
                {new Intl.DateTimeFormat("ru", { dateStyle: "medium" }).format(
                  new Date(user.created_at),
                )}
              </span>
              <Button onClick={() => setSelected(user)} tone="secondary">
                Управлять
              </Button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function EditUser({
  onCancel,
  onChanged,
  user,
}: {
  onCancel: () => void;
  onChanged: () => void;
  user: AdminUser;
}) {
  const [displayName, setDisplayName] = useState(user.display_name);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function updateProfile(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiRequest<AdminUser>(`/api/v1/admin/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({ display_name: displayName }),
      });
      onChanged();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось обновить участника",
      );
    } finally {
      setSaving(false);
    }
  }

  async function updateActive(isActive: boolean) {
    setSaving(true);
    setError("");
    try {
      await apiRequest<AdminUser>(`/api/v1/admin/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: isActive }),
      });
      onChanged();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось изменить доступ",
      );
    } finally {
      setSaving(false);
    }
  }

  async function updatePassword() {
    if (password.length < 12) {
      setError("Пароль должен быть минимум 12 символов");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await apiRequest<AdminUser>(`/api/v1/admin/users/${user.id}/password`, {
        method: "PATCH",
        body: JSON.stringify({ password }),
      });
      onChanged();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось сменить пароль",
      );
    } finally {
      setSaving(false);
    }
  }

  async function removeUser() {
    if (!window.confirm(`Удалить доступ администратора @${user.username}?`))
      return;
    setSaving(true);
    setError("");
    try {
      await apiRequest(`/api/v1/admin/users/${user.id}`, { method: "DELETE" });
      onChanged();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось удалить участника",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="inline-create team-editor" onSubmit={updateProfile}>
      <div className="inline-create__intro">
        <ShieldCheckIcon size={22} />
        <div>
          <strong>Управление администратором</strong>
          <span>@{user.username} · единый полный доступ без RBAC</span>
        </div>
      </div>
      <div className="inline-create__fields">
        <TextField
          label="Имя"
          onChange={(e) => setDisplayName(e.target.value)}
          required
          value={displayName}
        />
        <TextField
          helper="Минимум 12 символов"
          label="Новый пароль"
          minLength={12}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          value={password}
        />
      </div>
      {error && <span className="field__error">{error}</span>}
      <div className="inline-create__actions inline-create__actions--wrap">
        <Button disabled={saving} type="submit">
          Сохранить имя
        </Button>
        <Button
          disabled={saving || !password}
          onClick={() => void updatePassword()}
          tone="secondary"
          type="button"
        >
          <KeyIcon size={16} /> Сменить пароль
        </Button>
        <Button
          disabled={saving}
          onClick={() => void updateActive(!user.is_active)}
          tone="secondary"
          type="button"
        >
          {user.is_active ? "Отключить" : "Включить"}
        </Button>
        <Button
          disabled={saving}
          onClick={() => void removeUser()}
          tone="quiet"
          type="button"
        >
          <TrashIcon size={16} /> Удалить доступ
        </Button>
        <Button onClick={onCancel} tone="quiet" type="button">
          Закрыть
        </Button>
      </div>
    </form>
  );
}

function CreateUser({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await apiRequest<AdminUser>("/api/v1/admin/users", {
        method: "POST",
        body: JSON.stringify({ username, display_name: displayName, password }),
      });
      onCreated();
      onCancel();
    } catch (reason) {
      setError(
        reason instanceof ApiError
          ? reason.message
          : "Не удалось создать участника",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <form className="inline-create" onSubmit={submit}>
      <div className="inline-create__intro">
        <ShieldCheckIcon size={22} />
        <div>
          <strong>Новый администратор</strong>
          <span>Участник получит полный доступ к Command Center.</span>
        </div>
      </div>
      <div className="inline-create__fields">
        <TextField
          label="Имя"
          onChange={(e) => setDisplayName(e.target.value)}
          required
          value={displayName}
        />
        <TextField
          label="Логин"
          onChange={(e) => setUsername(e.target.value)}
          required
          value={username}
        />
        <TextField
          helper="Минимум 12 символов"
          label="Пароль"
          minLength={12}
          onChange={(e) => setPassword(e.target.value)}
          required
          type="password"
          value={password}
        />
      </div>
      {error && <span className="field__error">{error}</span>}
      <div className="inline-create__actions">
        <Button onClick={onCancel} tone="quiet" type="button">
          Отмена
        </Button>
        <Button disabled={saving} type="submit">
          Создать
        </Button>
      </div>
    </form>
  );
}
